import type { UseSheetData } from '@/hooks/useSheetData';
import { useMeterList } from '@/hooks/useMeterList';
import { MeterPicker } from '@/components/dashboard/MeterPicker';
import { AnomalySection } from '@/components/dashboard/AnomalySection';
import { PageLoadingSkeleton, PageErrorState, PageHeader } from '@/components/ui/PageState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import { Cpu } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

interface AnomalyPageProps { data: UseSheetData }

export function AnomalyPage({ data }: AnomalyPageProps) {
  const { rows, status, error } = data;
  const { meters, selectedMeter, setSelectedMeter, meterRows } = useMeterList(rows);
  const { t } = useLanguage();

  if (status === 'loading' && !rows.length) return <PageLoadingSkeleton />;
  if (status === 'error'   && !rows.length) return <PageErrorState message={error} onRetry={() => void data.refetch()} />;

  return (
    <div className="space-y-5 pt-3 pb-2 animate-fade-in">
      <PageHeader
        title={t("Anomaly")}
        description={t("Live status, risk indicators and threshold breach density.")}
      />

      <section className="space-y-2">
        <div className="flex items-end justify-between px-1">
          <h2 className="font-display text-base font-semibold text-ink-900">{t("Meter overview")}</h2>
          <span className="flex items-center gap-1 text-[11px] text-ink-500">
            <Cpu className="w-3.5 h-3.5" />
            {meters.length} {meters.length === 1 ? t('meter') : t('meters')}
          </span>
        </div>
        {meters.length ? (
          <MeterPicker meters={meters} value={selectedMeter} onChange={setSelectedMeter} />
        ) : null}
      </section>

      {!meterRows.length ? (
        <Card>
          <EmptyState
            icon={<Cpu className="w-8 h-8" />}
            title={t("No data for this meter yet")}
            description={t("Try another meter or wait for the next refresh.")}
          />
        </Card>
      ) : (
        <AnomalySection meterRows={meterRows} />
      )}
    </div>
  );
}